/**
 * Backload Section Component
 * Helps drivers find parcels/pallets to pick up on their return trip
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Package, MapPin, Calendar, Truck, Building2, Navigation, Search, RefreshCw, AlertCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { Map as MapComponent, MapControls, MapMarker, MarkerContent, MarkerTooltip, useMap } from '@/components/ui/map';
import DataTable from './DataTable';

interface Order {
  orderId: string;
  orderName?: string | null;
  originAddress?: string | null;
  originPostcode?: string | null;
  originLat?: number | null;
  originLon?: number | null;
  destinationAddress?: string | null;
  destinationPostcode?: string | null;
  destinationLat?: number | null;
  destinationLon?: number | null;
  weightKg?: number | null;
  pallets?: number;
  revenue?: number | null;
  cost?: number | null;
  currency?: string;
  status?: string;
  customerName?: string | null;
  companyName?: string | null;
  date?: string | null;
  _raw?: any; // Raw Qargo order data
}

interface BackloadSectionProps {}

// Haversine formula to calculate distance between two coordinates in kilometers
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Extract postcode from address string
function extractPostcode(address: string): string | null {
  if (!address) return null;
  const postcodeMatch = address.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}\b/i);
  return postcodeMatch ? postcodeMatch[0].trim() : null;
}

// Lookup postcode from UK postcodes CSV (fast, no API calls)
async function lookupPostcode(postcode: string): Promise<{ lat: number; lon: number } | null> {
  if (!postcode) return null;
  
  try {
    // Normalize postcode (remove spaces, uppercase)
    const normalized = postcode.toUpperCase().replace(/\s+/g, '');
    if (!normalized) return null;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const url = API_ENDPOINTS.postcodeLookup(normalized);
    console.log('📮 Postcode lookup:', normalized, '→', url);
    
    const response = await authenticatedFetch(url, {
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      if (data.ok && data.lat != null && data.lon != null) {
        const lat = parseFloat(String(data.lat));
        const lon = parseFloat(String(data.lon));
        if (!isNaN(lat) && !isNaN(lon) && isFinite(lat) && isFinite(lon)) {
          console.log('✅ Postcode lookup successful:', { lat, lon, source: data.source });
          return { lat, lon };
        }
      }
    }
    
    // Not found in CSV, return null (will fallback to geocoding API)
    return null;
  } catch (err) {
    if (err instanceof Error && err.name !== 'AbortError') {
      console.warn('Error looking up postcode:', postcode, err.message);
    }
    return null;
  }
}

// Geocode address/postcode using backend API (multi-provider with caching)
// First tries postcode lookup from CSV, then falls back to geocoding API
async function geocodeAddress(address: string, postcode?: string): Promise<{ lat: number; lon: number } | null> {
  if (!address && !postcode) return null;
  
  try {
    // Extract or use postcode
    let pc = postcode?.trim() || '';
    if (!pc && address) {
      pc = extractPostcode(address) || '';
    }
    
    // Try postcode lookup first (fast, from CSV)
    if (pc) {
      const csvResult = await lookupPostcode(pc);
      if (csvResult) {
        return csvResult;
      }
      console.log('📮 Postcode not found in CSV, falling back to geocoding API');
    }
    
    // Fallback to geocoding API
    let query = pc || address.trim();
    if (!query) return null;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    // Use backend geocoding API with UK as default country
    const url = API_ENDPOINTS.geocode(query, 'GB');
    console.log('🌍 Geocoding request:', query, '→', url);
    
    // Use authenticated fetch to automatically include JWT token
    const response = await authenticatedFetch(url, {
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Geocoding response:', data);
      if (data.ok && data.lat != null && data.lon != null) {
        const lat = parseFloat(String(data.lat));
        const lon = parseFloat(String(data.lon));
        if (!isNaN(lat) && !isNaN(lon) && isFinite(lat) && isFinite(lon)) {
          console.log('✅ Geocoding successful:', { lat, lon });
          return { lat, lon };
        } else {
          console.warn('⚠️ Invalid coordinates in response:', { lat, lon });
        }
      }
    } else if (response.status === 404) {
      // Geocoding failed - not found, return null gracefully
      console.warn('⚠️ Geocoding failed (404): Address not found');
      return null;
    } else {
      // Other error - log but don't throw
      const errorText = await response.text().catch(() => 'Unable to read error');
      console.warn('❌ Geocoding API error:', response.status, response.statusText, errorText);
      return null;
    }
    
    return null;
  } catch (err) {
    if (err instanceof Error && err.name !== 'AbortError') {
      console.warn('Error geocoding address:', address || postcode, err.message);
    }
    return null;
  }
}

export default function BackloadSection({}: BackloadSectionProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  
  // Filter states
  const [returnDate, setReturnDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [startPoint, setStartPoint] = useState<string>('');
  const [startPointCoords, setStartPointCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [availableCapacity, setAvailableCapacity] = useState<string>('');
  const [returnDestination, setReturnDestination] = useState<string>('');
  const [returnDestinationCoords, setReturnDestinationCoords] = useState<{ lat: number; lon: number } | null>(null);
  
  // Search state - controls geocoding progress
  const [isSearching, setIsSearching] = useState(false);
  
  // Autocomplete states
  const [startPointSuggestions, setStartPointSuggestions] = useState<Array<{ address: string; postcode: string; lat: number; lon: number }>>([]);
  const [showStartPointSuggestions, setShowStartPointSuggestions] = useState(false);
  const [returnDestinationSuggestions, setReturnDestinationSuggestions] = useState<Array<{ address: string; postcode: string; lat: number; lon: number }>>([]);
  const [showReturnDestinationSuggestions, setShowReturnDestinationSuggestions] = useState(false);
  
  // Map and selection states
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lon: number; zoom: number }>({
    lat: 51.5074, // Default to London
    lon: -0.1278,
    zoom: 6
  });

  // Debounce timeout ref for autocomplete
  const autocompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Search for address suggestions (autocomplete)
  const searchAddressSuggestions = useCallback(async (query: string, isReturnDestination: boolean) => {
    if (!query || query.length < 2) {
      if (isReturnDestination) {
        setReturnDestinationSuggestions([]);
        setShowReturnDestinationSuggestions(false);
      } else {
        setStartPointSuggestions([]);
        setShowStartPointSuggestions(false);
      }
      return;
    }

    try {
      const url = API_ENDPOINTS.geocodeAutocomplete(query, 'GB', 5);
      const response = await authenticatedFetch(url);
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.suggestions && data.suggestions.length > 0) {
          const suggestions = data.suggestions
            .filter((item: any) => item.lat != null && item.lon != null)
            .map((item: any) => ({
              address: item.address || item.postcode || query,
              postcode: item.postcode || '',
              lat: parseFloat(String(item.lat)),
              lon: parseFloat(String(item.lon))
            }));
          
          if (isReturnDestination) {
            setReturnDestinationSuggestions(suggestions);
            setShowReturnDestinationSuggestions(true);
          } else {
            setStartPointSuggestions(suggestions);
            setShowStartPointSuggestions(true);
          }
        } else {
          if (isReturnDestination) {
            setReturnDestinationSuggestions([]);
            setShowReturnDestinationSuggestions(false);
          } else {
            setStartPointSuggestions([]);
            setShowStartPointSuggestions(false);
          }
        }
      } else {
        if (isReturnDestination) {
          setReturnDestinationSuggestions([]);
          setShowReturnDestinationSuggestions(false);
        } else {
          setStartPointSuggestions([]);
          setShowStartPointSuggestions(false);
        }
      }
      } catch (err) {
        console.warn('Error fetching address suggestions:', err);
        if (isReturnDestination) {
          setReturnDestinationSuggestions([]);
          setShowReturnDestinationSuggestions(false);
        } else {
          setStartPointSuggestions([]);
          setShowStartPointSuggestions(false);
        }
      }
  }, []);

  // Fetch orders from Qargo (similar to OrdersSection)
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      setHasError(false);
      try {
        const url = API_ENDPOINTS.qargoOrders();
        console.log('📦 Fetching Qargo orders for backload:', url);
        const response = await authenticatedFetch(url);
        
        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { message: errorText };
          }
          throw new Error(errorData.error || errorData.message || `Failed to fetch orders: ${response.status}`);
        }
        
        const responseData = await response.json();
        console.log('✅ Qargo Orders Response:', {
          total: responseData.total,
          ordersCount: responseData.orders?.length || 0,
        });
        
        // Extract orders from response - API already maps them, but handle both formats
        let rawOrders: any[] = [];
        if (Array.isArray(responseData.orders)) {
          rawOrders = responseData.orders;
        } else if (responseData.all_orders && Array.isArray(responseData.all_orders)) {
          rawOrders = responseData.all_orders;
        } else if (Array.isArray(responseData)) {
          rawOrders = responseData;
        }
        
        // Map orders to our Order interface - handle both mapped (from API) and raw formats
        const mappedOrders: Order[] = rawOrders.map((order: any) => {
          // Check if order is already mapped (has pickup/delivery structure)
          const isMapped = order.pickup || order.delivery;
          
          if (isMapped) {
            // Order is already mapped by API - extract from mapped structure
            const raw = order._raw || {};
            const pallets = raw?.goods_pallet_spaces 
              ? parseInt(raw.goods_pallet_spaces, 10) 
              : (raw?.pallets ? parseInt(raw.pallets, 10) 
              : (order.pallets ? parseInt(order.pallets, 10) : 1));
            
            return {
              orderId: order.id || '',
              orderName: order.name || order.id || '',
              originAddress: order.pickup?.address || null,
              originPostcode: order.pickup?.postcode || null,
              originLat: order.pickup?.lat || null,
              originLon: order.pickup?.lon || null,
              destinationAddress: order.delivery?.address || null,
              destinationPostcode: order.delivery?.postcode || null,
              destinationLat: order.delivery?.lat || null,
              destinationLon: order.delivery?.lon || null,
              weightKg: order.weightKg || null,
              pallets: isNaN(pallets) ? 1 : pallets,
              revenue: order.revenue || null,
              cost: order.cost || null,
              currency: 'GBP', // Default, API should provide this
              status: order.status || 'UNKNOWN',
              customerName: order.customerRef || null,
              companyName: order.customerRef || null,
              // Use delivery date for backload filtering (when pallet needs to be delivered)
              date: order.delivery?.date || order.date || order.pickup?.date || null,
              _raw: raw || order
            };
          } else {
            // Order is raw - map from raw Qargo structure
            const pallets = order.goods_pallet_spaces 
              ? parseInt(order.goods_pallet_spaces, 10) 
              : (order.pallets ? parseInt(order.pallets, 10) : 1);
            
            const weightKg = order.goods_weight 
              ? parseFloat(order.goods_weight) 
              : (order.weight_kg ? parseFloat(order.weight_kg) 
              : (order.weightKg ? parseFloat(order.weightKg) : null));
            
            const revenue = order.total_revenue_tenant_currency 
              ? parseFloat(order.total_revenue_tenant_currency) 
              : (order.revenue ? parseFloat(order.revenue) : null);
            
            const companyName = order.customer || order.customer_name || order.customerRef || null;
            
            // Build origin address from components
            let originAddress = order.origin_address || null;
            if (!originAddress && (order.origin_name || order.origin_city)) {
              const parts = [
                order.origin_address,
                order.origin_city,
                order.origin_postal_code
              ].filter(Boolean);
              originAddress = parts.length > 0 ? parts.join(', ') : null;
            }
            
            // Build destination address from components
            let destinationAddress = order.destination_address || null;
            if (!destinationAddress && (order.destination_name || order.destination_city)) {
              const parts = [
                order.destination_address,
                order.destination_city,
                order.destination_postal_code
              ].filter(Boolean);
              destinationAddress = parts.length > 0 ? parts.join(', ') : null;
            }
            
            // Safely parse coordinates - handle empty strings, null, undefined
            let originLat: number | null = null;
            let originLon: number | null = null;
            if (order.origin_lat != null && order.origin_lat !== '') {
              const parsed = parseFloat(String(order.origin_lat));
              if (!isNaN(parsed) && isFinite(parsed)) {
                originLat = parsed;
              }
            }
            if (order.origin_lon != null && order.origin_lon !== '') {
              const parsed = parseFloat(String(order.origin_lon));
              if (!isNaN(parsed) && isFinite(parsed)) {
                originLon = parsed;
              }
            }
            
            let destinationLat: number | null = null;
            let destinationLon: number | null = null;
            if (order.destination_lat != null && order.destination_lat !== '') {
              const parsed = parseFloat(String(order.destination_lat));
              if (!isNaN(parsed) && isFinite(parsed)) {
                destinationLat = parsed;
              }
            }
            if (order.destination_lon != null && order.destination_lon !== '') {
              const parsed = parseFloat(String(order.destination_lon));
              if (!isNaN(parsed) && isFinite(parsed)) {
                destinationLon = parsed;
              }
            }
            
            return {
              orderId: order.order_id || order.id || '',
              orderName: order.name || order.order_id || '',
              originAddress: originAddress || order.origin_name || null,
              originPostcode: order.origin_postal_code || order.origin_postcode || null,
              originLat,
              originLon,
              destinationAddress: destinationAddress || order.destination_name || null,
              destinationPostcode: order.destination_postal_code || order.destination_postcode || null,
              destinationLat,
              destinationLon,
              weightKg: weightKg != null && !isNaN(weightKg) ? weightKg : null,
              pallets: isNaN(pallets) ? 1 : pallets,
              revenue: revenue != null && !isNaN(revenue) ? revenue : null,
              cost: order.total_cost_tenant_currency ? (() => {
                const parsed = parseFloat(String(order.total_cost_tenant_currency));
                return !isNaN(parsed) && isFinite(parsed) ? parsed : null;
              })() : (order.cost ? (() => {
                const parsed = parseFloat(String(order.cost));
                return !isNaN(parsed) && isFinite(parsed) ? parsed : null;
              })() : null),
              currency: order.tenant_currency || order.currency || 'GBP',
              status: order.status || 'UNKNOWN',
              customerName: companyName,
              companyName,
              // Use destination date for backload filtering (when pallet needs to be delivered)
              // Try destination_date first, then destination_timestamp, then fallback to origin_date
              date: order.destination_date || 
                    (order.destination_timestamp ? new Date(order.destination_timestamp).toISOString().split('T')[0] : null) ||
                    (order.destination_requested_start_timestamp ? new Date(order.destination_requested_start_timestamp).toISOString().split('T')[0] : null) ||
                    order.date || 
                    order.origin_date || 
                    null,
              _raw: order
            };
          }
        });
        
        // Don't geocode automatically on load - only geocode when user interacts with filters
        // Filter orders that are available for backload (not completed)
        // Only include orders that are NOT in completed/final states
        // This is fundamental - we should only show orders that can still be picked up
        const completedStatuses = [
          'COMPLETED', 
          'DELIVERED', 
          'CANCELLED', 
          'INVOICED', 
          'DONT_INVOICE',
          'INVOICE_POSTED', // Also filter invoice posted orders (like INVOICE_POSTED in CSV)
          'CLOSED',
          'FULFILLED'
        ];
        
        const availableOrders = mappedOrders.filter(order => {
          // If no status, include it (might be pending/new order)
          if (!order.status) return true;
          
          // Filter out completed/final statuses (case-insensitive, partial match)
          const statusUpper = order.status.toUpperCase().trim();
          const isCompleted = completedStatuses.some(completedStatus => 
            statusUpper === completedStatus || statusUpper.includes(completedStatus)
          );
          
          // Only return orders that are NOT completed
          return !isCompleted;
        });
        
        const excludedCount = mappedOrders.length - availableOrders.length;
        console.log(`📦 Filtered orders: ${availableOrders.length} available out of ${mappedOrders.length} total (excluded ${excludedCount} completed orders)`);
        
        setOrders(availableOrders);
      } catch (err) {
        console.error('❌ Error fetching orders:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch orders';
        setError(errorMessage);
        setHasError(true);
        // Set empty array to prevent further errors
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Wrap in try-catch to prevent errors from breaking the app
    try {
      fetchOrders().catch((err) => {
        console.error('❌ Unhandled error in fetchOrders:', err);
        setError('An unexpected error occurred. Please try refreshing the page.');
        setHasError(true);
        setOrders([]);
        setLoading(false);
      });
    } catch (err) {
      console.error('❌ Error setting up fetchOrders:', err);
      setError('Failed to initialize. Please try refreshing the page.');
      setHasError(true);
      setOrders([]);
      setLoading(false);
    }

    // Cleanup: clear autocomplete timeout on unmount
    return () => {
      if (autocompleteTimeoutRef.current) {
        clearTimeout(autocompleteTimeoutRef.current);
      }
    };
  }, []);

  // Calculate distance and filter orders based on user inputs
  // Show orders even if geocoding fails - just without distance calculations
  const filteredAndEnrichedOrders = useMemo(() => {
    try {
      // If no return destination coordinates, show all orders without distance
      if (!returnDestinationCoords) {
        return orders
          .map(order => {
            try {
              const revenuePerPallet = order.pallets && order.pallets > 0 && order.revenue 
                ? order.revenue / order.pallets 
                : (order.revenue || 0);
              return {
                ...order,
                distance: null,
                revenuePerPallet: isFinite(revenuePerPallet) ? revenuePerPallet : 0
              };
            } catch (err) {
              console.warn('Error processing order:', order.orderId, err);
              return null;
            }
          })
          .filter((order): order is Order & { distance: null; revenuePerPallet: number } => {
            if (!order) return false;
            
            // Filter by capacity if specified
            if (availableCapacity) {
              try {
                const capacity = parseFloat(availableCapacity);
                if (!isNaN(capacity) && isFinite(capacity) && order.weightKg && order.weightKg > capacity) {
                  return false;
                }
              } catch (err) {
                // Ignore capacity filter errors
              }
            }
            
            // Filter by date if specified - only show orders scheduled for the exact return trip date
            if (returnDate && order.date) {
              try {
                // Compare dates by YYYY-MM-DD (ignore time)
                const orderDateStr = new Date(order.date).toISOString().split('T')[0];
                const selectedDateStr = new Date(returnDate).toISOString().split('T')[0];
                // Only include orders scheduled for the exact return trip date
                if (orderDateStr !== selectedDateStr) {
                  return false;
                }
              } catch (err) {
                // Ignore date filter errors
              }
            }
            
            return true;
          })
          .sort((a, b) => {
            // Sort by revenue per pallet (highest first) when no distance
            return b.revenuePerPallet - a.revenuePerPallet;
          });
      }

      // If start point coordinates exist, calculate distances from start point to pallet location
      if (startPointCoords && 
          isFinite(startPointCoords.lat) && 
          isFinite(startPointCoords.lon)) {
        return orders
          .map(order => {
            try {
              // Show orders even without coordinates - they just won't have distance
              if (!order.originLat || !order.originLon || 
                  isNaN(order.originLat) || isNaN(order.originLon) ||
                  !isFinite(order.originLat) || !isFinite(order.originLon)) {
                // Still include order but without distance
                const revenuePerPallet = order.pallets && order.pallets > 0 && order.revenue 
                  ? order.revenue / order.pallets 
                  : (order.revenue || 0);
                return {
                  ...order,
                  distance: null,
                  revenuePerPallet: isFinite(revenuePerPallet) ? revenuePerPallet : 0
                };
              }
              
              // Calculate distance from start point to pallet location
              const distance = calculateDistance(
                startPointCoords.lat,
                startPointCoords.lon,
                order.originLat,
                order.originLon
              );
            
            if (!isFinite(distance) || isNaN(distance)) {
              // Distance calculation failed, but still show order
              const revenuePerPallet = order.pallets && order.pallets > 0 && order.revenue 
                ? order.revenue / order.pallets 
                : (order.revenue || 0);
              return {
                ...order,
                distance: null,
                revenuePerPallet: isFinite(revenuePerPallet) ? revenuePerPallet : 0
              };
            }
            
            const revenuePerPallet = order.pallets && order.pallets > 0 && order.revenue 
              ? order.revenue / order.pallets 
              : (order.revenue || 0);
            
            return {
              ...order,
              distance,
              revenuePerPallet: isFinite(revenuePerPallet) ? revenuePerPallet : 0
            };
          } catch (err) {
            console.warn('Error processing order:', order.orderId, err);
            return null;
          }
        })
        .filter((order): order is Order & { distance: number | null; revenuePerPallet: number } => {
          if (!order) return false;
          
          // Filter by capacity if specified
          if (availableCapacity) {
            try {
              const capacity = parseFloat(availableCapacity);
              if (!isNaN(capacity) && isFinite(capacity) && order.weightKg && order.weightKg > capacity) {
                return false;
              }
            } catch (err) {
              // Ignore capacity filter errors
            }
          }
          
          // Filter by date if specified
          if (returnDate && order.date) {
            try {
              const orderDate = new Date(order.date);
              const selectedDate = new Date(returnDate);
              if (orderDate < selectedDate) {
                return false;
              }
            } catch (err) {
              // Ignore date filter errors
            }
          }
          
          return true;
        })
        .sort((a, b) => {
          try {
            // Sort by distance if available (closest first)
            if (a.distance != null && b.distance != null) {
              if (a.distance !== b.distance) {
                return a.distance - b.distance;
              }
            } else if (a.distance != null) {
              return -1; // Orders with distance come first
            } else if (b.distance != null) {
              return 1;
            }
            // Then by revenue per pallet (highest first)
              return b.revenuePerPallet - a.revenuePerPallet;
            } catch (err) {
              console.warn('Error sorting orders:', err);
              return 0;
            }
          });
      }
      
      // If no start point, show orders without distance (same as no return destination case)
      return orders
        .map(order => {
          try {
            const revenuePerPallet = order.pallets && order.pallets > 0 && order.revenue 
              ? order.revenue / order.pallets 
              : (order.revenue || 0);
            return {
              ...order,
              distance: null,
              revenuePerPallet: isFinite(revenuePerPallet) ? revenuePerPallet : 0
            };
          } catch (err) {
            console.warn('Error processing order:', order.orderId, err);
            return null;
          }
        })
        .filter((order): order is Order & { distance: null; revenuePerPallet: number } => {
          if (!order) return false;
          
          // Filter by capacity if specified
          if (availableCapacity) {
            try {
              const capacity = parseFloat(availableCapacity);
              if (!isNaN(capacity) && isFinite(capacity) && order.weightKg && order.weightKg > capacity) {
                return false;
              }
            } catch (err) {
              // Ignore capacity filter errors
            }
          }
          
          // Filter by date if specified
          if (returnDate && order.date) {
            try {
              const orderDate = new Date(order.date);
              const selectedDate = new Date(returnDate);
              if (orderDate < selectedDate) {
                return false;
              }
            } catch (err) {
              // Ignore date filter errors
            }
          }
          
          return true;
        })
        .sort((a, b) => {
          // Sort by revenue per pallet (highest first) when no distance
          return b.revenuePerPallet - a.revenuePerPallet;
        });
    } catch (err) {
      console.error('Error in filteredAndEnrichedOrders:', err);
      // Return empty array on error to prevent crashes
      return [];
    }
  }, [orders, startPointCoords, availableCapacity, returnDate]);

  // Extract unique pallet locations for map
  const palletLocations = useMemo(() => {
    const locationMap = new Map<string, Order & { distance: number | null; revenuePerPallet: number }>();
    
    filteredAndEnrichedOrders.forEach(order => {
      // Only include orders with valid coordinates
      if (!order.originLat || !order.originLon || 
          isNaN(order.originLat) || isNaN(order.originLon) ||
          !isFinite(order.originLat) || !isFinite(order.originLon)) {
        return;
      }
      
      const key = `${order.originLat.toFixed(4)},${order.originLon.toFixed(4)}`;
      const existing = locationMap.get(key);
      
      // Keep the order with highest revenue per pallet at this location
      if (!existing || order.revenuePerPallet > existing.revenuePerPallet) {
        locationMap.set(key, order);
      }
    });
    
    const locations = Array.from(locationMap.values());
    console.log('📍 Pallet locations for map:', locations.length, 'locations from', filteredAndEnrichedOrders.length, 'orders');
    console.log('📍 Sample locations:', locations.slice(0, 3).map(loc => ({ 
      id: loc.orderId, 
      lat: loc.originLat, 
      lon: loc.originLon,
      address: loc.originAddress 
    })));
    return locations;
  }, [filteredAndEnrichedOrders]);

  // Update map center when locations change
  useEffect(() => {
    try {
      if (palletLocations.length > 0) {
        const lats = palletLocations.map(loc => loc.originLat).filter((lat): lat is number => lat != null && isFinite(lat));
        const lons = palletLocations.map(loc => loc.originLon).filter((lon): lon is number => lon != null && isFinite(lon));
        if (lats.length > 0 && lons.length > 0) {
          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
          if (isFinite(centerLat) && isFinite(centerLon)) {
            setMapCenter({ lat: centerLat, lon: centerLon, zoom: 10 });
          }
        }
      } else if (returnDestinationCoords && 
                 isFinite(returnDestinationCoords.lat) && 
                 isFinite(returnDestinationCoords.lon)) {
        // If no pallets but return destination is set, center on that
        setMapCenter({ 
          lat: returnDestinationCoords.lat, 
          lon: returnDestinationCoords.lon, 
          zoom: 12 
        });
      }
    } catch (err) {
      console.warn('Error updating map center:', err);
      // Keep default center on error
    }
  }, [palletLocations, returnDestinationCoords]);

  // Handle finding backload - geocodes locations and updates table/map progressively
  const handleFindBackload = useCallback(async () => {
    setIsSearching(true);
    
    try {
      // Geocode start point if provided
      if (startPoint.trim()) {
        const postcodeMatch = startPoint.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}\b/i);
        const postcode = postcodeMatch ? postcodeMatch[0] : undefined;
        const geocoded = await geocodeAddress(startPoint, postcode);
        if (geocoded) {
          setStartPointCoords(geocoded);
        } else {
          // Try to find in existing orders
          const matchingOrder = orders.find(order => 
            order.originAddress?.toLowerCase().includes(startPoint.toLowerCase()) ||
            order.originPostcode?.toLowerCase().includes(startPoint.toLowerCase())
          );
          if (matchingOrder?.originLat && matchingOrder?.originLon) {
            setStartPointCoords({ lat: matchingOrder.originLat, lon: matchingOrder.originLon });
          } else {
            setStartPointCoords(null);
          }
        }
      } else {
        setStartPointCoords(null);
      }
      
      // Geocode return destination if provided
      if (returnDestination.trim()) {
        const postcodeMatch = returnDestination.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}\b/i);
        const postcode = postcodeMatch ? postcodeMatch[0] : undefined;
        const geocoded = await geocodeAddress(returnDestination, postcode);
        if (geocoded) {
          setReturnDestinationCoords(geocoded);
        } else {
          // Try to find in existing orders
          const matchingOrder = orders.find(order => 
            order.destinationAddress?.toLowerCase().includes(returnDestination.toLowerCase()) ||
            order.destinationPostcode?.toLowerCase().includes(returnDestination.toLowerCase())
          );
          if (matchingOrder?.destinationLat && matchingOrder?.destinationLon) {
            setReturnDestinationCoords({ lat: matchingOrder.destinationLat, lon: matchingOrder.destinationLon });
          } else if (matchingOrder?.originLat && matchingOrder?.originLon) {
            setReturnDestinationCoords({ lat: matchingOrder.originLat, lon: matchingOrder.originLon });
          } else {
            setReturnDestinationCoords(null);
          }
        }
      } else {
        setReturnDestinationCoords(null);
      }
      
      // Geocode orders that don't have coordinates (optimized batch lookup)
      const ordersToGeocode = orders.filter(order => 
        (!order.originLat || !order.originLon || 
         isNaN(order.originLat) || isNaN(order.originLon) ||
         !isFinite(order.originLat) || !isFinite(order.originLon)) &&
        (order.originAddress || order.originPostcode)
      );
      
      if (ordersToGeocode.length > 0) {
        console.log(`🌍 Processing ${ordersToGeocode.length} orders without coordinates...`);
        
        // Step 1: Extract all postcodes for batch lookup
        const postcodesToLookup: string[] = [];
        const orderPostcodeMap = new Map<string, Order[]>(); // postcode -> orders[]
        
        for (const order of ordersToGeocode) {
          const address = order.originAddress || '';
          const postcode = order.originPostcode || '';
          const extractedPostcode = extractPostcode(address) || postcode.trim();
          
          if (extractedPostcode) {
            const normalized = extractedPostcode.toUpperCase().replace(/\s+/g, '');
            if (!orderPostcodeMap.has(normalized)) {
              postcodesToLookup.push(normalized);
              orderPostcodeMap.set(normalized, []);
            }
            orderPostcodeMap.get(normalized)!.push(order);
          }
        }
        
        // Step 2: Batch lookup postcodes from CSV (fast, no API calls)
        if (postcodesToLookup.length > 0) {
          console.log(`📮 Batch looking up ${postcodesToLookup.length} unique postcodes from CSV...`);
          
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
            
            const response = await authenticatedFetch(API_ENDPOINTS.postcodeBatchLookup(), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ postcodes: postcodesToLookup }),
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (response.ok) {
              const data = await response.json();
              const { results } = data;
              
              // Step 3: Update orders with CSV lookup results (progressive update)
              let updatedCount = 0;
              for (const [postcode, coords] of Object.entries(results)) {
                const ordersForPostcode = orderPostcodeMap.get(postcode) || [];
                for (const order of ordersForPostcode) {
                  if (coords && typeof coords === 'object' && 'lat' in coords && 'lon' in coords) {
                    const lat = parseFloat(String(coords.lat));
                    const lon = parseFloat(String(coords.lon));
                    if (isFinite(lat) && isFinite(lon)) {
                      setOrders(prevOrders => 
                        prevOrders.map(o => 
                          o.orderId === order.orderId
                            ? { ...o, originLat: lat, originLon: lon }
                            : o
                        )
                      );
                      updatedCount++;
                    }
                  }
                }
              }
              
              console.log(`✅ Batch lookup: Found ${data.found}/${data.total} postcodes, updated ${updatedCount} orders`);
            } else {
              console.warn('⚠️ Batch postcode lookup failed, falling back to individual geocoding');
            }
          } catch (err) {
            console.warn('⚠️ Batch postcode lookup error, falling back to individual geocoding:', err);
          }
        }
        
        // Step 4: For orders still without coordinates, use geocoding API (fallback)
        // Wait a moment for state updates to propagate
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Get current orders state to check which still need geocoding
        // We'll use a ref or check the orders that weren't in the batch lookup results
        const ordersNeedingGeocoding = ordersToGeocode.filter(order => {
          // Check if this order's postcode was in our batch lookup
          const address = order.originAddress || '';
          const postcode = order.originPostcode || '';
          const extractedPostcode = extractPostcode(address) || postcode.trim();
          const normalized = extractedPostcode ? extractedPostcode.toUpperCase().replace(/\s+/g, '') : '';
          
          // If postcode was in batch lookup, assume it was handled (state will update)
          // If no postcode, needs geocoding API
          return !normalized || !postcodesToLookup.includes(normalized);
        });
        
        if (ordersNeedingGeocoding.length > 0) {
          console.log(`🌍 Geocoding ${ordersNeedingGeocoding.length} remaining orders via API (no postcode or not in CSV)...`);
          
          // Process remaining orders in smaller batches for API rate limiting
          const batchSize = 5;
          const delayBetweenBatches = 1200; // 1.2 seconds between batches
          
          for (let i = 0; i < ordersNeedingGeocoding.length; i += batchSize) {
            const batch = ordersNeedingGeocoding.slice(i, i + batchSize);
            const batchNum = Math.floor(i / batchSize) + 1;
            const totalBatches = Math.ceil(ordersNeedingGeocoding.length / batchSize);
            console.log(`🌍 Geocoding API batch ${batchNum}/${totalBatches} (${batch.length} orders)`);
            
            // Process batch in parallel but update state after each order completes
            const batchPromises = batch.map(async (order) => {
              try {
                const address = order.originAddress || '';
                const postcode = order.originPostcode || '';
                
                if (!address.trim() && !postcode.trim()) {
                  return null;
                }
                
                const geocoded = await geocodeAddress(address, postcode);
                if (geocoded && isFinite(geocoded.lat) && isFinite(geocoded.lon)) {
                  // Update orders state immediately for this order (progressive update)
                  setOrders(prevOrders => 
                    prevOrders.map(o => 
                      o.orderId === order.orderId
                        ? { ...o, originLat: geocoded.lat, originLon: geocoded.lon }
                        : o
                    )
                  );
                  console.log(`✅ Geocoded order ${order.orderId}: ${geocoded.lat}, ${geocoded.lon}`);
                  return { orderId: order.orderId, coords: geocoded };
                } else {
                  console.warn(`⚠️ Failed to geocode order ${order.orderId} (${address || postcode})`);
                  return null;
                }
              } catch (err) {
                console.warn(`⚠️ Error geocoding order ${order.orderId}:`, err);
                return null;
              }
            });
            
            await Promise.all(batchPromises);
            
            // Delay between batches (except for the last batch)
            if (i + batchSize < ordersNeedingGeocoding.length) {
              await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
            }
          }
          
          console.log(`✅ Finished geocoding remaining orders`);
        }
        
        console.log(`✅ Finished processing orders`);
      } else {
        console.log('✅ All orders already have coordinates');
      }
    } catch (err) {
      console.error('Error finding backload:', err);
      // Continue showing results even if geocoding partially failed
    } finally {
      setIsSearching(false);
    }
  }, [startPoint, returnDestination, orders]);

  // Handle geocoding for start point and return destination (for autocomplete selection)


  // Handle row click to zoom map
  const handleRowClick = useCallback((orderId: string) => {
    try {
      setSelectedOrderId(orderId);
      const order = filteredAndEnrichedOrders.find(o => o.orderId === orderId);
      if (order && order.originLat != null && order.originLon != null &&
          isFinite(order.originLat) && isFinite(order.originLon)) {
        setMapCenter({
          lat: order.originLat,
          lon: order.originLon,
          zoom: 15
        });
      }
    } catch (err) {
      console.warn('Error handling row click:', err);
      // Don't break the UI on row click errors
    }
  }, [filteredAndEnrichedOrders]);

  // Prepare table data
  const tableData = useMemo(() => {
    try {
      return filteredAndEnrichedOrders.map(order => {
        try {
          return {
            id: order.orderId,
            palletLocation: (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-ring" />
                <div>
                  <div className="font-medium text-white">{order.originAddress || order.originPostcode || 'Unknown'}</div>
                  {order.originPostcode && order.originAddress && (
                    <div className="text-xs text-muted-foreground">{order.originPostcode}</div>
                  )}
                </div>
              </div>
            ),
            distance: order.distance != null && isFinite(order.distance) ? (
              <div className="text-right">
                <div className="font-medium text-white">{order.distance.toFixed(1)} km</div>
              </div>
            ) : (
              <div className="text-right text-muted-foreground">-</div>
            ),
            numberOfPallets: (
              <div className="text-center">
                <div className="font-medium text-white">{order.pallets || '-'}</div>
              </div>
            ),
            revenuePerPallet: order.revenuePerPallet != null && isFinite(order.revenuePerPallet) ? (
              <div className="text-right">
                <div className="font-medium text-success">
                  {order.currency || '£'}{order.revenuePerPallet.toFixed(2)}
                </div>
              </div>
            ) : (
              <div className="text-right text-muted-foreground">-</div>
            ),
            company: (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-foreground">{order.companyName || order.customerName || 'Unknown'}</span>
              </div>
            ),
            _order: order // Store order for reference
          };
        } catch (err) {
          console.warn('Error formatting table row:', order.orderId, err);
          return null;
        }
      }).filter((row): row is NonNullable<typeof row> => row !== null);
    } catch (err) {
      console.error('Error preparing table data:', err);
      return [];
    }
  }, [filteredAndEnrichedOrders]);

  // Map controller component to handle zooming
  const MapController = ({ selectedOrderId }: { selectedOrderId: string | null }) => {
    const { map, isLoaded } = useMap();

    useEffect(() => {
      if (!isLoaded || !map || !selectedOrderId) return;

      const order = filteredAndEnrichedOrders.find(o => o.orderId === selectedOrderId);
      if (order && order.originLat != null && order.originLon != null && 
          !isNaN(order.originLat) && !isNaN(order.originLon)) {
        map.flyTo({
          center: [order.originLon, order.originLat],
          zoom: 15,
          duration: 1000,
        });
      }
    }, [isLoaded, map, selectedOrderId, filteredAndEnrichedOrders]);

    return null;
  };

  // If there's a critical error, show a friendly message but don't crash
  if (hasError && !loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-brand/30 via-card to-background rounded-xl border border-brand/20 p-6 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-8 h-8 text-brand" />
            <h2 className="text-2xl font-bold text-white">Backload Finder</h2>
          </div>
          <p className="text-muted-foreground">
            Find available pallets to pick up on your return trip
          </p>
        </div>
        
        <div className="bg-destructive/20 border border-destructive/50 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-destructive mb-2">Unable to Load Backload Data</h3>
              <p className="text-foreground mb-4">
                {error || 'An error occurred while loading backload information.'}
              </p>
              <button
                onClick={() => {
                  setHasError(false);
                  setError(null);
                  window.location.reload();
                }}
                className="px-4 py-2 bg-brand hover:bg-brand text-white rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-brand/30 via-card to-background rounded-xl border border-brand/20 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <Package className="w-8 h-8 text-brand" />
          <h2 className="text-2xl font-bold text-white">Backload Finder</h2>
        </div>
        <p className="text-foreground text-sm">
          Maximize your return trip by finding available parcels and pallets along your route back to base.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Return Date */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Calendar className="w-4 h-4" />
              Return Trip Date
            </label>
            <input
              type="date"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          {/* Start Point */}
          <div className="space-y-2 relative">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <MapPin className="w-4 h-4" />
              Start Point
            </label>
            <div className="relative">
              <input
                type="text"
                value={startPoint}
                onChange={(e) => {
                  const value = e.target.value;
                  setStartPoint(value);
                  
                  // Clear previous timeout
                  if (autocompleteTimeoutRef.current) {
                    clearTimeout(autocompleteTimeoutRef.current);
                  }
                  
                  // Debounce autocomplete search
                  autocompleteTimeoutRef.current = setTimeout(() => {
                    searchAddressSuggestions(value, false);
                  }, 300);
                }}
                onFocus={() => {
                  if (startPointSuggestions.length > 0) {
                    setShowStartPointSuggestions(true);
                  }
                }}
                onBlur={() => {
                  // Delay hiding suggestions to allow click
                  setTimeout(() => {
                    setShowStartPointSuggestions(false);
                  }, 200);
                }}
                placeholder="Enter address or postcode"
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand"
              />
              {showStartPointSuggestions && startPointSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
                  {startPointSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setStartPoint(suggestion.address);
                        setStartPointCoords({ lat: suggestion.lat, lon: suggestion.lon });
                        setShowStartPointSuggestions(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-secondary text-white text-sm transition-colors"
                    >
                      <div className="font-medium">{suggestion.address}</div>
                      {suggestion.postcode && (
                        <div className="text-xs text-muted-foreground">{suggestion.postcode}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Available Capacity */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Truck className="w-4 h-4" />
              Available Capacity (kg)
            </label>
            <input
              type="number"
              value={availableCapacity}
              onChange={(e) => setAvailableCapacity(e.target.value)}
              placeholder="e.g. 5000"
              className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          {/* Return Destination */}
          <div className="space-y-2 relative">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Navigation className="w-4 h-4" />
              Return Destination
            </label>
            <div className="relative">
              <input
                type="text"
                value={returnDestination}
                onChange={(e) => {
                  const value = e.target.value;
                  setReturnDestination(value);
                  
                  // Clear previous timeout
                  if (autocompleteTimeoutRef.current) {
                    clearTimeout(autocompleteTimeoutRef.current);
                  }
                  
                  // Debounce autocomplete search
                  autocompleteTimeoutRef.current = setTimeout(() => {
                    searchAddressSuggestions(value, true);
                  }, 300);
                }}
                onFocus={() => {
                  if (returnDestinationSuggestions.length > 0) {
                    setShowReturnDestinationSuggestions(true);
                  }
                }}
                onBlur={() => {
                  // Delay hiding suggestions to allow click
                  setTimeout(() => {
                    setShowReturnDestinationSuggestions(false);
                  }, 200);
                }}
                placeholder="Enter base address or postcode"
                className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand"
              />
              {showReturnDestinationSuggestions && returnDestinationSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
                  {returnDestinationSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setReturnDestination(suggestion.address);
                        setReturnDestinationCoords({ lat: suggestion.lat, lon: suggestion.lon });
                        setShowReturnDestinationSuggestions(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-secondary text-white text-sm transition-colors"
                    >
                      <div className="font-medium">{suggestion.address}</div>
                      {suggestion.postcode && (
                        <div className="text-xs text-muted-foreground">{suggestion.postcode}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Find Backload Button */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            <button
              onClick={handleFindBackload}
              disabled={isSearching || !returnDate}
              className="flex items-center gap-2 px-6 py-3 bg-brand hover:bg-brand disabled:bg-muted disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-brand/50"
            >
              {isSearching ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Find Backload
                </>
              )}
            </button>
            <span className="text-sm text-muted-foreground">
              {isSearching ? (
                <span>Geocoding locations and calculating distances...</span>
              ) : (
                <>
                  Found <span className="font-semibold text-white">{filteredAndEnrichedOrders.length}</span> available pallets
                  {returnDate && (
                    <span className="text-muted-foreground ml-2">
                      • Scheduled for {new Date(returnDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                  )}
                  {startPointCoords && (
                    <span className="text-muted-foreground ml-2">
                      • Showing distances from start point
                    </span>
                  )}
                </>
              )}
            </span>
          </div>
          <button
            onClick={() => {
              setStartPoint('');
              setAvailableCapacity('');
              setReturnDestination('');
              setReturnDestinationCoords(null);
              setStartPointCoords(null);
            }}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-white bg-secondary hover:bg-accent rounded-lg transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table and Map Layout - Always visible, updates as geocoding progresses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Table - Left Side */}
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            {loading ? (
            <div className="flex items-center justify-center h-full p-12">
              <RefreshCw className="w-8 h-8 text-brand animate-spin" />
              <span className="ml-3 text-muted-foreground">Loading available pallets...</span>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <div className="text-destructive mb-2">{error}</div>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-brand hover:bg-brand text-white rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          ) : tableData.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-16 h-16 text-accent mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Available Pallets</h3>
              <p className="text-muted-foreground text-sm">
                {returnDestination 
                  ? 'No pallets found matching your criteria. Try adjusting your filters.'
                  : 'Enter your return destination to see available pallets along your route.'}
              </p>
            </div>
          ) : (
            <div className="max-h-150 overflow-y-auto">
              <DataTable
                data={tableData}
                columns={[
                  { key: 'palletLocation', label: 'Pallet Location' },
                  { key: 'distance', label: 'Distance (km)', align: 'right' },
                  { key: 'numberOfPallets', label: 'Pallets', align: 'center' },
                  { key: 'revenuePerPallet', label: 'Revenue/Pallet', align: 'right' },
                  { key: 'company', label: 'Company' },
                ]}
                defaultSort={{ column: 'distance', direction: 'asc' }}
                maxRows={10}
                hoverColor="purple"
                onRowClick={(row) => {
                  if (row._order) {
                    handleRowClick(row._order.orderId);
                    // Scroll to map if needed (for mobile/small screens)
                    setTimeout(() => {
                      const mapElement = document.querySelector('[data-map-container]');
                      if (mapElement) {
                        mapElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }
                    }, 100);
                  }
                }}
                selectedRowId={selectedOrderId}
                rowIdKey="id"
              />
            </div>
          )}
        </div>

        {/* Map - Right Side */}
        <div className="bg-card rounded-xl border border-border overflow-hidden relative" style={{ height: '600px', minHeight: '600px' }} data-map-container>
          <div className="w-full h-full">
            <MapComponent
              center={isFinite(mapCenter.lat) && isFinite(mapCenter.lon) 
                ? [mapCenter.lon, mapCenter.lat] 
                : [-0.1278, 51.5074]} // Default to London if invalid
              zoom={mapCenter.zoom}
              theme="dark"
            >
            <MapController selectedOrderId={selectedOrderId} />
            <MapControls />
            
            {/* Mark return destination */}
            {returnDestinationCoords && 
             isFinite(returnDestinationCoords.lat) && 
             isFinite(returnDestinationCoords.lon) && (
              <MapMarker
                longitude={returnDestinationCoords.lon}
                latitude={returnDestinationCoords.lat}
              >
                <MarkerContent>
                  <div className="bg-destructive text-white px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg flex items-center gap-1">
                    <Navigation className="w-3 h-3" />
                    <span>Return Base</span>
                  </div>
                </MarkerContent>
                <MarkerTooltip>
                  <div className="text-sm font-medium">Return Destination</div>
                  <div className="text-xs text-foreground">{returnDestination}</div>
                </MarkerTooltip>
              </MapMarker>
            )}
            
            {/* Mark start point */}
            {startPointCoords && 
             isFinite(startPointCoords.lat) && 
             isFinite(startPointCoords.lon) && (
              <MapMarker
                longitude={startPointCoords.lon}
                latitude={startPointCoords.lat}
              >
                <MarkerContent>
                  <div className="bg-ring text-white px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span>Start Point</span>
                  </div>
                </MarkerContent>
                <MarkerTooltip>
                  <div className="text-sm font-medium">Start Point</div>
                  <div className="text-xs text-foreground">{startPoint}</div>
                </MarkerTooltip>
              </MapMarker>
            )}
            
            {/* Mark pallet locations */}
            {palletLocations.map((order) => {
              if (!order.originLat || !order.originLon || 
                  !isFinite(order.originLat) || !isFinite(order.originLon)) {
                return null;
              }
              return (
                <MapMarker
                  key={order.orderId}
                  longitude={order.originLon}
                  latitude={order.originLat}
                >
                <MarkerContent>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg transition-all cursor-pointer ${
                    selectedOrderId === order.orderId 
                      ? 'bg-warning text-black ring-2 ring-warning ring-offset-2 scale-110' 
                      : 'bg-success text-white hover:bg-success hover:scale-105'
                  }`}>
                    <div className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      <span>{order.pallets || 1} {order.pallets === 1 ? 'Pallet' : 'Pallets'}</span>
                    </div>
                  </div>
                </MarkerContent>
                <MarkerTooltip>
                  <div className="text-sm font-medium">{order.orderName || order.orderId}</div>
                  <div className="text-xs text-foreground">{order.originAddress || order.originPostcode}</div>
                  {order.distance != null && isFinite(order.distance) && (
                    <div className="text-xs text-ring mt-1">
                      {order.distance.toFixed(1)} km from start point
                    </div>
                  )}
                  {order.revenuePerPallet > 0 && (
                    <div className="text-xs text-success mt-1">
                      {order.currency || '£'}{order.revenuePerPallet.toFixed(2)} per pallet
                    </div>
                  )}
                </MarkerTooltip>
              </MapMarker>
              );
            })}
            </MapComponent>
          </div>
        </div>
      </div>
    </div>
  );
}

